/**
 * Which tenant every tenant-scoped request belongs to.
 *
 * The API mounts every tenant-scoped path TWICE:
 *
 *   canonical   /api/v1/t/{slug}/overview
 *   legacy      /api/v1/overview            → always the default tenant
 *
 * so a client with no tenant selected still reaches a working console. That is
 * what makes this module a single mutable slug rather than a required argument
 * on two hundred call sites: `tenantPath("/overview")` returns "/overview"
 * before a tenant is known, which is the legacy alias, which is the default
 * tenant. It NEVER invents a slug — guessing "default" here would make a
 * request to the wrong tenant look like a request to the right one.
 *
 * The slug is module state, not React state, because the axios layer and the
 * download-href builders are not components and must not need a hook. The one
 * writer is <TenantProvider> (src/lib/tenant.tsx), which sets it during render
 * before it renders any child, so no query can be issued against the previous
 * tenant's slug.
 *
 * Platform paths — /tenants, /modules/platform, /platform/settings,
 * /admin/users, /admin/audit, /auth/* — must NOT go through tenantPath. They
 * belong to the deployment, not to a tenant.
 */

let currentTenant: string | null = null;

/**
 * Point every subsequent tenant-scoped request at this slug. `null` returns to
 * the legacy alias, which the server resolves to the default tenant.
 */
export function setCurrentTenant(slug: string | null): void {
  currentTenant = slug === null || slug === "" ? null : slug;
}

/** The slug in effect, or null when no tenant has been selected. */
export function currentTenantSlug(): string | null {
  return currentTenant;
}

/**
 * The tenant-scoped form of an API path.
 *
 * `tenantPath("/runs/abc")` → "/t/acme/runs/abc", or "/runs/abc" when no
 * tenant is selected. The argument is the path AFTER /api/v1 and must start
 * with a slash.
 */
export function tenantPath(path: string): string {
  if (currentTenant === null) return path;
  return `/t/${encodeURIComponent(currentTenant)}${path}`;
}

/**
 * The tenant element every tenant-scoped query key carries.
 *
 * It is the second element of every key factory, so switching tenants produces
 * a DIFFERENT cache entry rather than showing the previous tenant's rows while
 * the new ones load. `null` is a real value here — the legacy alias is its own
 * scope — and is never collapsed into the string "default".
 */
export function tenantScopeKey(): string | null {
  return currentTenant;
}
